import { TsDOM as q } from '@/scripts/core/TsDOM'
import { CartNotification } from '@/scripts/theme/cart-notification'
import { CartDrawer } from '@/scripts/cart/cart-drawer'
import { ModalDialog } from '@/scripts/theme/modal-dialog'
import type {
	CartItem,
	Cart,
	CartErrorResponse,
	ProductVariant,
} from '@/scripts/shopify'
import type { ShopifySectionRenderingSchema } from '@/scripts/types/theme'
import { DynamicProgressBar } from '@/scripts/cart/dynamic-progress-bar'
import {
	persistCartSnapshot,
	updateCartSnapshot,
} from '@/scripts/core/product-cache'
import {
	cartDebug,
	summarizeCartItems,
	summarizeInstructions,
} from '@/scripts/cart/optimistic-debug'
import { ensureDiscountAppliedFromStorage } from '@/scripts/discounts/url-discount'

type SectionsResponse = Record<string, string>

function hasUsableSectionsResponse(
	sections: unknown,
	requestedSections: string[]
): sections is SectionsResponse {
	if (!sections || typeof sections !== 'object') return false

	const sectionRecord = sections as Record<string, unknown>
	return requestedSections.some((sectionHandle) => {
		const html = sectionRecord[sectionHandle]
		return typeof html === 'string' && html.trim() !== ''
	})
}

export type CartAdd = {
	items: CartItem[]
}

export type CartAddWithSections = CartAdd & {
	sections: SectionsResponse
	_conflictsResolved?: boolean
}

export type CartUpdateWithSections = Cart & {
	sections: SectionsResponse
}

export type CartUpdateInstructions = {
	updates: Record<number, number> | Record<string, number>
	additions: AddToCartItem[]
	update_required: boolean
}

export function isStandaloneTrialCartItem(item: CartItem): boolean {
	const properties = item.properties ?? {}
	const isTrial = String(properties._is_trial ?? '').toLowerCase() === 'true'
	if (!isTrial) return false

	return !(
		Boolean(properties._bundle_membership_item) ||
		Boolean(properties._alcm_item) ||
		Boolean(properties._activation_item)
	)
}

function hasAssociatedMembershipProperties(
	properties: Record<string, string> | undefined
): boolean {
	return Boolean(
		properties?._bundle_membership_item ||
		properties?._alcm_item ||
		properties?._activation_item
	)
}

export function isMembershipCartItem(item: CartItem): boolean {
	const productType = (item.product_type ?? '').trim().toLowerCase()
	return (
		item.title.toLowerCase().includes('membership') ||
		Boolean(item.properties?._bundle_membership_item) ||
		Boolean(item.properties?._alcm_item) ||
		Boolean(item.properties?._activation_item) ||
		productType === 'streaming' ||
		productType.includes('membership')
	)
}

export function isBundleCartItem(item: CartItem): boolean {
	const productType = (item.product_type ?? '').trim().toLowerCase()
	const handle = (item.handle ?? '').trim().toLowerCase()
	return (
		item.title.toLowerCase().includes('bundle') ||
		productType === 'bundle' ||
		handle.includes('bundle') ||
		Boolean(item.properties?._bundle_offer)
	)
}

function normalizePayLater(value: unknown): string | null {
	if (typeof value !== 'string') return null
	const normalized = value.trim()
	return normalized || null
}

export function hasMatchingBundleInCart(
	items: CartItem[],
	variantId: number,
	payLater: string | null
): boolean {
	const targetPayLater = normalizePayLater(payLater)

	return items.some(
		(item) =>
			item.id === variantId &&
			isBundleCartItem(item) &&
			normalizePayLater(item.properties?._pay_later) === targetPayLater
	)
}

export function isExclusiveCartItem(item: CartItem): boolean {
	return (
		isMembershipCartItem(item) ||
		isBundleCartItem(item) ||
		isStandaloneTrialCartItem(item)
	)
}

/**
 * Calculate which existing cart items conflict with items being added.
 * Returns keys to remove and parent IDs to track for removal.
 */
function calculateExclusiveConflicts(
	currentItems: CartItem[],
	itemsToPreserve: Set<string>
): {
	keysToRemove: string[]
	keysToCapAtOne: string[]
	parentProductIdsToRemove: Set<number>
} {
	const keysToRemove = new Set<string>()
	const keysToCapAtOne: string[] = []
	const parentProductIdsToRemove = new Set<number>()

	for (const item of currentItems) {
		if (itemsToPreserve.has(item.key)) {
			if (isExclusiveCartItem(item) && item.quantity > 1) {
				keysToCapAtOne.push(item.key)
			}
			continue
		}

		const shouldRemoveForConflict = isExclusiveCartItem(item)
		cartDebug('calculateExclusiveConflicts:evaluating-item', {
			key: item.key,
			title: item.title,
			productType: item.product_type,
			handle: item.handle,
			isExclusive: shouldRemoveForConflict,
			properties: item.properties,
		})
		if (shouldRemoveForConflict) {
			keysToRemove.add(item.key)

			if (hasAssociatedMembershipProperties(item.properties)) {
				const bundleParentIdRaw =
					item.properties?._bundle_parent_id ?? ''
				const bundleParentId = Number(bundleParentIdRaw)
				if (
					Number.isSafeInteger(bundleParentId) &&
					bundleParentId > 0
				) {
					parentProductIdsToRemove.add(bundleParentId)
				}
			}
		}
	}

	// Remove only true bundle parent products when associated memberships are removed.
	// A la carte parent products should remain in cart as regular products.
	if (parentProductIdsToRemove.size > 0) {
		for (const item of currentItems) {
			if (itemsToPreserve.has(item.key) || keysToRemove.has(item.key))
				continue
			if (!parentProductIdsToRemove.has(item.product_id)) continue

			const isAssociatedMembership = hasAssociatedMembershipProperties(
				item.properties
			)
			const isGwp = Boolean(item.properties?._gwp_item)
			const isBundleParent = isBundleCartItem(item)

			cartDebug('calculateExclusiveConflicts:parent-candidate', {
				key: item.key,
				productId: item.product_id,
				title: item.title,
				productType: item.product_type,
				handle: item.handle,
				isAssociatedMembership,
				isGwp,
				isBundleParent,
			})

			if (isAssociatedMembership || isGwp || !isBundleParent) {
				continue
			}

			keysToRemove.add(item.key)
		}
	}

	return {
		keysToRemove: Array.from(keysToRemove),
		keysToCapAtOne,
		parentProductIdsToRemove,
	}
}

/**
 * Batched cart update: adds new items AND removes conflicting items in ONE request.
 * This avoids the race condition and reduces round trips from 2 to 1.
 */
async function addItemsWithConflictResolution(
	itemsToAdd: AddToCartItem[],
	sections?: string[]
): Promise<CartAdd | CartAddWithSections | CartErrorResponse> {
	cartDebug('addItemsWithConflictResolution:start', {
		itemsToAdd: summarizeCartItems(itemsToAdd),
		sections,
	})

	// Step 1: Get current cart state FIRST (before any modifications)
	const currentCart = await getCart()
	if ('status' in currentCart) {
		return currentCart
	}

	cartDebug('addItemsWithConflictResolution:current-cart', {
		itemCount: currentCart.items.length,
		items: summarizeCartItems(currentCart.items),
	})

	// Step 2: Always calculate conflicts based on CURRENT cart items
	// The cart might already have exclusive items (bundles/memberships) that
	// conflict with ANY new item being added. We can't rely on properties of
	// items being added since Replo and other flows don't set them.
	const keysToPreserve = new Set(
		currentCart.items
			.filter((item) =>
				itemsToAdd.some(
					(addItem) =>
						addItem.id === item.id &&
						hasMatchingBundleInCart(
							[item],
							addItem.id,
							addItem.properties._pay_later ?? null
						)
				)
			)
			.map((item) => item.key)
	)

	const conflicts = calculateExclusiveConflicts(
		currentCart.items,
		keysToPreserve
	)
	const keysToRemove = conflicts.keysToRemove
	const keysToCapAtOne = conflicts.keysToCapAtOne

	cartDebug('addItemsWithConflictResolution:conflicts', {
		keysToRemove,
		keysToCapAtOne,
		keysToPreserve: Array.from(keysToPreserve),
		conflictCount: keysToRemove.length,
		currentCartItems: summarizeCartItems(currentCart.items),
	})

	// Step 4: Prepare the batched request
	// Format items for cart/update.js endpoint
	const newItemsFormatted = itemsToAdd.map((item) => ({
		id: item.id,
		quantity: item.quantity,
	}))

	const existingItemsFormatted = [
		...keysToRemove.map((key) => ({ key, quantity: 0 })),
		...keysToCapAtOne.map((key) => ({ key, quantity: 1 })),
	]

	// Step 5: Single request to /cart/update.js that handles both add and remove
	const sectionsToRequest = sections?.length
		? sections
		: [
				'cart-drawer-items',
				'cart-icon-bubble',
				'dynamic-progress-bar',
				'dynamic-cart-footer',
				'cart-update-instructions',
			]

	cartDebug('addItemsWithConflictResolution:batched-request', {
		newItems: newItemsFormatted.length,
		itemsToRemove: existingItemsFormatted.length,
	})

	// Detect same-variant conflicts: if a variant being added shares a line
	// with a variant being removed, we MUST NOT use the batched
	// /cart/update.js path. Shopify's /cart/update.js only modifies existing
	// lines — it does not create new ones — and when both updates target the
	// same underlying line (one by variant ID, one by line key) it resolves
	// to "remove" with no new line created.
	//
	// This is the bug when a pay-later bundle is in cart and the user
	// triggers a regular add of the same bundle: the pay-later line is
	// correctly flagged for removal, but the "new" bundle add collides with
	// it on variant ID, so nothing gets re-added. Forcing the line-context
	// path does remove-then-/cart/add.js, which creates a fresh line with
	// the new (empty) properties and lets the server-side offer rules
	// attach the membership.
	const removedVariantIds = new Set<number>()
	for (const item of currentCart.items) {
		if (keysToRemove.includes(item.key)) {
			removedVariantIds.add(item.id)
		}
	}
	const hasSameVariantAsRemoval = itemsToAdd.some((addItem) =>
		removedVariantIds.has(addItem.id)
	)

	const requiresLineContext =
		hasSameVariantAsRemoval ||
		itemsToAdd.some(
			(item) =>
				item.selling_plan != null ||
				Object.keys(item.properties ?? {}).length > 0
		)

	if (requiresLineContext) {
		cartDebug('addItemsWithConflictResolution:line-context-fallback', {
			items: summarizeCartItems(itemsToAdd),
			itemsToRemove: existingItemsFormatted.length,
			hasSameVariantAsRemoval,
		})

		if (existingItemsFormatted.length > 0) {
			const cleanupResult = await updateCartRequest(
				[],
				existingItemsFormatted
			)
			if ('status' in cleanupResult) {
				return cleanupResult as CartErrorResponse
			}
		}

		const addResult = await addItemsToCart(itemsToAdd, sectionsToRequest, {
			conflictMode: 'none',
		})

		if ('status' in addResult) {
			return addResult as CartErrorResponse
		}

		if ('sections' in addResult) {
			return {
				...addResult,
				_conflictsResolved: existingItemsFormatted.length > 0,
			}
		}

		return {
			items: addResult.items,
			sections: {},
			_conflictsResolved: existingItemsFormatted.length > 0,
		}
	}

	const result = await updateCartRequest(
		newItemsFormatted,
		existingItemsFormatted,
		sectionsToRequest
	)

	if ('status' in result) {
		cartDebug('addItemsWithConflictResolution:error', {
			status: result.status,
			description: result.description,
		})
		return result as CartErrorResponse
	}

	const resultHasSections =
		'sections' in result &&
		hasUsableSectionsResponse(result.sections, sectionsToRequest)

	cartDebug('addItemsWithConflictResolution:success', {
		itemCount: result.item_count,
		hasSections: resultHasSections,
		items: summarizeCartItems(result.items || []),
	})

	// Convert Cart to CartAddWithSections format
	const sectionsResponse: SectionsResponse = resultHasSections
		? (result as CartUpdateWithSections).sections
		: {}
	const addResult: CartAddWithSections = {
		items: result.items || [],
		sections: sectionsResponse,
		_conflictsResolved: keysToRemove.length > 0,
	}

	return addResult
}

export async function getCart(): Promise<Cart | CartErrorResponse> {
	try {
		cartDebug('getCart:start')
		const cart = await fetch('/cart.js', {
			method: 'GET',
			headers: {
				'Content-Type': 'application/json',
			},
		}).then((response) => response.json())
		if (cart.status) {
			cartDebug('getCart:error-response', {
				status: cart.status,
				description: cart.description,
			})
			return cart as CartErrorResponse
		}
		// Keep the cart snapshot current for synchronous membership checks
		updateCartSnapshot(cart.items)
		persistCartSnapshot(cart)
		cartDebug('getCart:success', {
			itemCount: cart.item_count,
			items: summarizeCartItems(cart.items),
		})
		return cart as Cart
	} catch (error) {
		cartDebug('getCart:exception', {
			error: error instanceof Error ? error.message : String(error),
		})
		console.error(error)
		return {
			status: 500,
			description: 'Error in HTTP request',
			message:
				'This cart operation failed, but Shopify did not return an error',
		}
	}
}

export function getDOMCart() {
	return (
		q.os<CartNotification>(CartNotification.htmlSelector) ??
		q.os<CartDrawer>(CartDrawer.htmlSelector)
	)
}

export function hasDomCart() {
	return getDOMCart() !== undefined
}

export function openCartDrawerWithOptimisticItem(
	data: import('@/scripts/cart/optimistic-cart').OptimisticItemData,
	associated?: import('@/scripts/cart/optimistic-cart').AssociatedOptimisticItem[]
): void {
	cartDebug('openCartDrawerWithOptimisticItem', {
		mainVariantId: data.variantId,
		mainSortGroup: data.sortGroup,
		associated: (associated ?? []).map((item) => ({
			type: item.type,
			variantId: item.variantId,
			title: item.title,
		})),
	})
	const cartEl = getDOMCart()
	if (cartEl && 'openWithOptimisticItem' in cartEl) {
		;(cartEl as CartDrawer).openWithOptimisticItem(data, associated)
	} else {
		cartDebug('openCartDrawerWithOptimisticItem:no-drawer')
	}
}

export function getDOMCartSectionApiIds() {
	const cartEl = getDOMCart()
	if (!cartEl) return undefined
	return [
		'cart-drawer-items',
		'cart-icon-bubble',
		'dynamic-progress-bar',
		'dynamic-cart-footer',
		'cart-update-instructions',
	]
}

export function updateProgressBar(rawHTML: string) {
	const shippingBar = q.os<DynamicProgressBar>('dynamic-progress-bar')
	const freeShippingInfo = q.os<HTMLElement>('[data-free-shipping-info]')
	if (!shippingBar) return

	const newDocument = new DOMParser().parseFromString(rawHTML, 'text/html')
	const newShippingBar = q.rs<DynamicProgressBar>(
		DynamicProgressBar.htmlSelector,
		newDocument
	)
	const newFreeShippingInfo = q.os<HTMLElement>(
		'.drawer__free-shipping',
		newDocument
	)

	if (newFreeShippingInfo) {
		if (freeShippingInfo) {
			freeShippingInfo.innerHTML = newFreeShippingInfo.outerHTML
			freeShippingInfo.style.removeProperty('display')
		}
		shippingBar.style.setProperty('display', 'none')
		return
	}

	if (freeShippingInfo) {
		freeShippingInfo.style.setProperty('display', 'none')
	}
	shippingBar.style.removeProperty('display')

	if (newShippingBar) {
		shippingBar.animateFromRawHTML(newShippingBar.outerHTML)
	}
}

export async function renderResponseToCartDrawer(
	cart: CartAddWithSections | CartUpdateWithSections | CartAdd,
	modal?: ModalDialog
): Promise<void> {
	cartDebug('renderResponseToCartDrawer:start', {
		hasSections: 'sections' in cart && Boolean(cart.sections),
		itemCount: 'items' in cart ? cart.items.length : undefined,
	})
	if (modal) {
		await new Promise<void>((resolve) => {
			document.body.addEventListener(
				'modalClosed',
				() => {
					setTimeout(async () => {
						await renderResponseToCartDrawer(cart)
						resolve()
					})
				},
				{ once: true }
			)
			modal.hide(true)
		})
		return
	}

	const cartEl = getDOMCart()

	if (!cartEl) {
		window.location.href = window.routes.cart_url
		return
	}

	if ('sections' in cart && cart.sections) {
		cartDebug('renderResponseToCartDrawer:renderContents', {
			sections: Object.keys(cart.sections),
		})
		await cartEl.renderContents(cart)
	} else {
		cartDebug('renderResponseToCartDrawer:refreshCartDrawer')
		// No usable section HTML (e.g. Replo page) — re-fetch cart with sections
		await refreshCartDrawer(cartEl)
	}
}

async function refreshCartDrawer(cartEl: CartDrawer | CartNotification) {
	try {
		const sections = [
			'cart-drawer-items',
			'cart-icon-bubble',
			'dynamic-progress-bar',
			'dynamic-cart-footer',
			'cart-update-instructions',
		]
		const response = await fetch(
			window.routes.cart_update_url,
			configPostRequest({
				updates: {},
				sections,
				sections_url: window.routes.cart_url,
			})
		)
		if (!response.ok) {
			cartEl.open()
			return
		}
		const cart = await response.json()
		if (cart.sections) {
			await cartEl.renderContents(cart)
		} else {
			cartEl.open()
		}
	} catch {
		cartEl.open()
	}
}

export type AddToCartItem = {
	id: number
	quantity: number
	selling_plan?: number
	properties: Record<string, string>
}

export type AddItemsConflictMode = 'none' | 'exclusive_only'

type RenderRawHTMLToDOMInput = {
	sourceHTML: string
	sourceSelector?: string
	destinationSelectorContainer?: string
	destinationSelector: string
}

export function renderRawHTMLToDOM({
	sourceHTML,
	sourceSelector,
	destinationSelector,
	destinationSelectorContainer,
}: RenderRawHTMLToDOMInput) {
	const sourceSelectorOrDefault = sourceSelector ?? '.shopify-section'
	const newDocument = new DOMParser().parseFromString(sourceHTML, 'text/html')
	const sourceElement = q.rs(
		sourceSelectorOrDefault,
		newDocument.documentElement
	)
	if (destinationSelectorContainer) {
		const container = q.rs(destinationSelectorContainer)
		const destinationElement = q.rs(destinationSelector, container)
		if (destinationElement.outerHTML === sourceElement.outerHTML) return
		destinationElement.outerHTML = sourceElement.outerHTML
	} else {
		const destinationElement = q.rs(destinationSelector)
		if (destinationElement.outerHTML === sourceElement.outerHTML) return
		destinationElement.outerHTML = sourceElement.outerHTML
	}
}

export function configPostRequest(data: unknown) {
	return {
		method: 'POST',
		headers: {
			'Content-Type': 'application/json',
			Accept: `application/json`,
			'X-Requested-With': 'XMLHttpRequest',
		},
		body: JSON.stringify(data),
	}
}

export async function addItemsToCart(
	items: AddToCartItem[],
	sections?: string[],
	options?: { conflictMode?: AddItemsConflictMode }
): Promise<CartAdd | CartAddWithSections | CartErrorResponse> {
	const conflictMode = options?.conflictMode ?? 'none'

	cartDebug('addItemsToCart:start', {
		items: summarizeCartItems(items),
		sections,
		conflictMode,
	})

	// Use batched conflict resolution only for exclusive flows.
	// Standard adds should not evict existing exclusive items.
	if (conflictMode === 'exclusive_only') {
		cartDebug('addItemsToCart:using-batched-approach')
		const result = await addItemsWithConflictResolution(items, sections)
		if (!('status' in result)) {
			void ensureDiscountAppliedFromStorage('add_to_cart').catch(
				() => false
			)
		}
		return result
	}

	// Standard /cart/add.js path for non-exclusive additions.
	const data =
		sections && sections.length
			? {
					items,
					sections,
					sections_url: window.routes.cart_url,
				}
			: { items }
	try {
		const response = await fetch(
			`${window.routes.cart_add_url}`,
			configPostRequest(data)
		)
		const cart = await response.json()
		if (!response.ok || cart.status) {
			cartDebug('addItemsToCart:error-response', {
				status: cart.status || response.status,
				description:
					cart.description || cart.message || response.statusText,
			})
			return {
				status: cart.status || response.status,
				description:
					cart.description || cart.message || response.statusText,
				message: cart.message || cart.description || 'Cart add failed',
			} as CartErrorResponse
		}

		const addResult: CartAddWithSections | CartAdd = sections
			? (cart as CartAddWithSections)
			: (cart as CartAdd)

		void ensureDiscountAppliedFromStorage('add_to_cart').catch(() => false)

		cartDebug('addItemsToCart:success', {
			items: summarizeCartItems(addResult.items),
			hasSections: 'sections' in addResult,
		})

		return addResult
	} catch (error) {
		cartDebug('addItemsToCart:exception', {
			error: error instanceof Error ? error.message : String(error),
		})
		console.error(error)
		return {
			status: 500,
			description: 'Error in HTTP request',
			message:
				'This cart operation failed, but Shopify did not return an error',
		}
	}
}

type CartUpdateByKeyItem = {
	key: string
	quantity: number
}

type CartUpdateByIdItem = {
	id: number
	quantity: number
}

export async function updateCartRequest(
	newItems: CartUpdateByIdItem[],
	existingItems: CartUpdateByKeyItem[],
	sections?: string[]
): Promise<Cart | CartUpdateWithSections | CartErrorResponse> {
	const updates: Record<number | string, number> = {}
	newItems.forEach((item) => {
		updates[item.id] = item.quantity
	})
	existingItems.forEach((item) => {
		updates[item.key] = item.quantity
	})
	const data =
		sections && sections.length
			? {
					updates,
					sections,
					sections_url: window.routes.cart_url,
				}
			: {
					updates,
				}

	cartDebug('updateCartRequest:start', {
		updates,
		sections,
	})

	try {
		const response = await fetch(
			`${window.routes.cart_update_url}`,
			configPostRequest(data)
		)
		const cart = await response.json()
		if (cart.status) {
			cartDebug('updateCartRequest:error-response', {
				status: cart.status,
				description: cart.description,
			})
			return cart as CartErrorResponse
		}
		if (cart.sections && cart.sections['cart-update-instructions']) {
			const instructions = getUpdateInstructions(
				cart as CartUpdateWithSections
			)
			cartDebug('updateCartRequest:instructions', {
				instructions: summarizeInstructions(instructions),
			})
		}
		cartDebug('updateCartRequest:success', {
			itemCount: cart.item_count,
			items: summarizeCartItems(cart.items ?? []),
			hasSections: Boolean(cart.sections),
		})
		if (sections?.length) {
			return cart as CartUpdateWithSections
		}
		return cart as Cart
	} catch (error) {
		cartDebug('updateCartRequest:exception', {
			error: error instanceof Error ? error.message : String(error),
		})
		console.error(error)
		return {
			status: 500,
			description: 'Error in HTTP request',
			message:
				'This cart operation failed, but Shopify did not return an error',
		}
	}
}

export type ShopifyProductOptions = {
	option1: string
	option2?: string
	option3?: string
}

export function variantFromProductOptions(
	options: ShopifyProductOptions,
	variants: ProductVariant[]
): ProductVariant {
	const variant = variants.find((variant) => {
		if (variant.option1 !== options.option1) return false
		if (options.option2 && variant.option2 !== options.option2) return false
		if (options.option3 && variant.option3 !== options.option3) return false
		return true
	})
	if (!variant) throw Error('No variant found')
	return variant
}

export async function getSectionHTMLForResource(
	url: string,
	sectionId: string
): Promise<string | undefined> {
	try {
		return await fetch(`${url}?sections=${sectionId}`)
			.then((response) => {
				return response.json()
			})
			.then((response) => {
				const html = response[sectionId]
				const parser = new DOMParser()
				const doc = parser.parseFromString(html, 'text/html')
				return q.rs('.shopify-section', doc).innerHTML
			})
	} catch (e) {
		console.error('error in getSectionHTMLForResource')
		console.log(e)
	}
}

export function getActiveOrAccessibilityElement(): HTMLElement {
	const activeElement = document.activeElement
	if (activeElement instanceof HTMLElement) {
		return activeElement
	} else {
		return q.rs('[data-uc-accessibility-focus]')
	}
}

/**
 * Render section HTML to DOM in a single pass.
 * Used by finalRender() for the atomic swap after all locks release.
 */
export function renderSectionsOnce(
	sections: SectionsResponse,
	sectionSchemas: ShopifySectionRenderingSchema[]
): void {
	for (const schema of sectionSchemas) {
		const sectionHandle = schema.section
		if (!sectionHandle || sectionHandle === 'cart-update-instructions')
			continue
		const sectionHTML = sections[sectionHandle]
		if (!sectionHTML) continue
		if (sectionHandle === 'dynamic-progress-bar') {
			updateProgressBar(sectionHTML)
			continue
		}
		renderRawHTMLToDOM({
			sourceHTML: sectionHTML,
			sourceSelector: schema.selector,
			destinationSelector: schema.selector,
			destinationSelectorContainer: `#${schema.id}`,
		})
	}
}

export function getUpdateInstructions(
	cart: CartAddWithSections | CartUpdateWithSections
): CartUpdateInstructions {
	const noUpdate: CartUpdateInstructions = {
		update_required: false,
		updates: {},
		additions: [],
	}
	try {
		const responseHTML = cart.sections?.['cart-update-instructions']
		if (!responseHTML) return noUpdate
		const newDocument = new DOMParser().parseFromString(
			responseHTML,
			'text/html'
		)
		const scriptEl = newDocument.querySelector<HTMLScriptElement>(
			'[data-cart-update-instructions]'
		)
		if (!scriptEl?.textContent) return noUpdate
		const body = JSON.parse(scriptEl.textContent)
		if (
			'update_required' in body &&
			typeof body.update_required === 'boolean' &&
			'updates' in body &&
			typeof body.updates === 'object' &&
			'additions' in body &&
			Array.isArray(body.additions)
		) {
			return body
		}
		console.warn(
			'[CartUpdateInstructions] Unexpected shape:',
			scriptEl.textContent
		)
		return noUpdate
	} catch (e) {
		console.warn(
			'[CartUpdateInstructions] Parse error, returning default:',
			e
		)
		return noUpdate
	}
}
